// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { RoleEnum } from './Enums'
import { EthAddr, ReportingColumnVisibilityEnum } from './SchemaTypes'

export enum TicketStatusEnum {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
}

import * as yup from 'yup'
import { yupEthAddrRequired } from './Enums'
import { StringTypeEnum, toFieldMetaData } from './SchemaTypes'
import { EventNameForTemplate, EventTypeForTemplate } from './templateApx'

export const TicketCTOSchema = yup.object({
  id: yup
    .string()
    .required()
    .label('Ticket ID')
    .meta(
      toFieldMetaData({
        propName: 'id',
        description: 'Unique identifier for the ticket',
      })
    ),
  ticketStatus: yup
    .mixed<TicketStatusEnum>()
    .oneOf(Object.values(TicketStatusEnum))
    .required()
    .label('Ticket Status')
    .meta(
      toFieldMetaData({
        propName: 'ticketStatus',
        description: 'Current status of the ticket',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 1,
      })
    ),
  poolAddr: yupEthAddrRequired.label('Vault Address').meta(
    toFieldMetaData({
      propName: 'poolAddr',
      description: 'Pool (vault) address for which the ticket is created',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  poolName: yup
    .string()
    .required()
    .label('Pool Name')
    .meta(
      toFieldMetaData({
        propName: 'poolName',
        description: 'Name of the pool/vault',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
      })
    ),
  roleEnum: yup
    .mixed<RoleEnum>()
    .oneOf(Object.values(RoleEnum))
    .required()
    .label('Role')
    .meta(
      toFieldMetaData({
        propName: 'roleEnum',
        description: 'Role associated with the ticket',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 2,
      })
    ),
  lenderAddr: yup
    .string()
    .optional()
    .label('Lender Address')
    .meta(
      toFieldMetaData({
        propName: 'lenderAddr',
        description: 'Optional lender address associated with this ticket',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  companyName: yup
    .string()
    .optional()
    .label('Company Name')
    .meta(
      toFieldMetaData({
        propName: 'companyName',
        description: 'Optional company name related to the ticket',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 3,
      })
    ),
  eventType: yup
    .mixed<EventTypeForTemplate>()
    .oneOf(Object.values(EventTypeForTemplate))
    .required()
    .label('Event Type')
    .meta(
      toFieldMetaData({
        propName: 'eventType',
        description: 'Event Type for which the ticket was created',
      })
    ),
  eventName: yup
    .mixed<EventNameForTemplate>()
    .oneOf(Object.values(EventNameForTemplate))
    .required()
    .label('Event Type')
    .meta(
      toFieldMetaData({
        propName: 'eventName',
        description: 'Event Type for which the ticket was created',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 4,
      })
    ),
  label: yup
    .string()
    .required()
    .label('Label')
    .meta(
      toFieldMetaData({
        propName: 'label',
        description: 'Label or title for the ticket',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 5,
      })
    ),

  link: yup
    .string()
    .required()
    .label('Link')
    .meta(
      toFieldMetaData({
        propName: 'link',
        description: 'Relevant link for this ticket, if any',
      })
    ),
  ticketTimestamp: yup
    .number()
    .required()
    .label('Ticket Timestamp')
    .meta(
      toFieldMetaData({
        propName: 'ticketTimestamp',
        description: 'Timestamp when the ticket was created',
        stringType: StringTypeEnum.ETH_DATE,
      })
    ),
  ticketTimestampDateString: yup
    .string()
    .required()
    .label('Ticket Timestamp Date String')
    .meta(
      toFieldMetaData({
        propName: 'ticketTimestampDateString',
        description: 'Date string for the ticket timestamp',
        stringType: StringTypeEnum.ETH_DATE_TIME_STRING,
      })
    ),
  completedTimestamp: yup
    .number()
    .optional()
    .label('Completed Timestamp')
    .meta(
      toFieldMetaData({
        propName: 'completedTimestamp',
        description: 'Timestamp when the ticket was completed',
        stringType: StringTypeEnum.ETH_DATE,
      })
    ),
  completedTimestampDateString: yup
    .string()
    .optional()
    .label('Completed Timestamp Date String')
    .meta(
      toFieldMetaData({
        propName: 'completedTimestampDateString',
        description: 'Date string for the completed timestamp',
        stringType: StringTypeEnum.ETH_DATE_TIME_STRING,
      })
    ),
  sequenceId: yup
    .string()
    .optional()
    .label('Sequence ID')
    .meta(
      toFieldMetaData({
        propName: 'sequenceId',
        description: 'Sequence ID for the ticket',
      })
    ),
  autoComplete: yup
    .boolean()
    .required()
    .label('Auto Complete')
    .meta(
      toFieldMetaData({
        propName: 'autoComplete',
        description: 'Auto complete the ticket',
      })
    ),
})

export interface TicketCTO {
  id: string
  ticketStatus: TicketStatusEnum
  poolAddr: EthAddr
  poolName: string
  roleEnum: RoleEnum
  lenderAddr?: EthAddr
  companyName?: string
  eventType: EventTypeForTemplate
  eventName: EventNameForTemplate
  label: string
  link: string
  ticketTimestamp: number
  ticketTimestampDateString: string
  completedTimestamp?: number
  completedTimestampDateString?: string
  sequenceId?: string
  autoComplete: boolean
}
type TicketCTOInfer = yup.InferType<typeof TicketCTOSchema>
export const _testATicket: TicketCTO = {} as unknown as TicketCTOInfer
export const _testBTicket: TicketCTOInfer = {} as unknown as TicketCTOInfer
